/**
 * BadgeCelebration — the full-screen award moment. Mounted ONCE at App level;
 * watches MY earned-badge stream (the MyBadgesProvider's single subscription)
 * and celebrates every badge that appears AFTER the seed snapshot — the seed
 * itself (badges you already had when the page loaded) never celebrates.
 * Multiple awards queue and play sequentially: badge art large inside a
 * tier-colored ring glow, name + criteria, a Deej congratulation, CSS
 * confetti. Dismiss via the button, Escape, or the ~8s auto-timer.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useMyBadgeRows } from '../services/useUserBadges.js';
import { BADGES } from './badgeManifest.js';

/** How long one celebration lingers before it dismisses itself. */
export const CELEBRATION_AUTO_DISMISS_MS = 8_000;

const CONFETTI_PIECES = 18;

/** Deej's congratulation repertoire — picked deterministically per badge. */
const DEEJ_LINES = [
  'Deej saw the whole thing. Magnificent.',
  'Coo! That one goes straight on the mantle.',
  'Deej is strutting in circles about this.',
  'A shiny for the nest — Deej approves.',
  'Deej puffed up with pride. Literally.',
  'The flock has been notified. Coo-gratulations!',
];

function deejLine(badgeId: string): string {
  let h = 0;
  for (const ch of badgeId) h = (h + ch.charCodeAt(0)) % DEEJ_LINES.length;
  return DEEJ_LINES[h];
}

export function BadgeCelebration() {
  const { uid, rows } = useMyBadgeRows();
  const [queue, setQueue] = useState<string[]>([]);
  // The last snapshot we compared against, keyed by uid — `null` means the
  // NEXT emission is a seed (never celebrated).
  const seenRef = useRef<{ uid: string; ids: Set<string> } | null>(null);

  useEffect(() => {
    if (!uid) {
      // Signed out: forget everything so the next sign-in reseeds cleanly.
      seenRef.current = null;
      setQueue((q) => (q.length ? [] : q));
      return;
    }
    if (rows === null) return; // subscription reset — awaiting the new seed
    const ids = new Set(rows.map((r) => r.badgeId));
    const seen = seenRef.current;
    if (!seen || seen.uid !== uid) {
      // The seed snapshot (or a user switch): record, never celebrate.
      seenRef.current = { uid, ids };
      if (seen && seen.uid !== uid) setQueue([]);
      return;
    }
    const fresh = rows
      .map((r) => r.badgeId)
      .filter((id) => !seen.ids.has(id) && BADGES.some((b) => b.id === id));
    seenRef.current = { uid, ids };
    if (fresh.length) setQueue((q) => [...q, ...fresh]);
  }, [uid, rows]);

  const dismiss = useCallback(() => setQueue((q) => q.slice(1)), []);

  const current = queue[0];
  useEffect(() => {
    if (!current) return;
    const timer = setTimeout(dismiss, CELEBRATION_AUTO_DISMISS_MS);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [current, dismiss]);

  if (!current) return null;
  // Present by construction — unknown ids are filtered at enqueue time.
  const art = BADGES.find((b) => b.id === current)!;

  return (
    <div
      className={`badge-celebration badge-celebration--tier${art.tier}`}
      data-testid="badge-celebration"
      data-badge-id={art.id}
      role="status"
      aria-live="polite"
    >
      <div className="badge-celebration__confetti" aria-hidden="true">
        {Array.from({ length: CONFETTI_PIECES }, (_, i) => (
          <span
            key={i}
            className="badge-celebration__piece"
            style={{ '--n': String(i) } as CSSProperties}
          />
        ))}
      </div>
      <div className="badge-celebration__card">
        <div className="badge-celebration__ring">
          <img className="badge-celebration__img" src={art.src} alt={art.alt} />
        </div>
        <div className="badge-celebration__kicker">Badge earned!</div>
        <h2 className="badge-celebration__name">{art.name}</h2>
        <div className="badge-celebration__criteria">{art.criteria}</div>
        <div className="badge-celebration__line">{deejLine(art.id)}</div>
        <button
          className="btn btn--primary"
          onClick={dismiss}
          data-testid="badge-celebration-dismiss"
        >
          Coo! Continue
        </button>
      </div>
    </div>
  );
}
